/**
 * Composable Vue partagé pour l'état d'authentification.
 *
 * Persistance : localStorage (clés `sapiendo.auth.access`, `sapiendo.auth.refresh`, `sapiendo.auth.user`).
 * Pas de Pinia : un état réactif global suffit pour ce périmètre.
 */

import { computed, ref } from 'vue'

import { STORAGE_RETURN_TO } from '@/maintenance/storageKeys'

import type { AuthUser, TokenResponse } from './types'

/**
 * Changement d'identité (connexion, déconnexion, entrée et sortie d'impersonation) : les états en mémoire d'un
 * agent qui portent des données du conseiller précédent (stores d'artefacts, de collecte) s'y abonnent pour se
 * réinitialiser. Pattern observer, comme `onSessionExpired` d'`api.ts` : aucun couplage de ce module du shell
 * vers un agent. Un même listener n'est enregistré qu'une fois. Renvoie la fonction de désabonnement.
 */
type SessionChangedListener = () => void
const sessionChangedListeners = new Set<SessionChangedListener>()

export function onSessionChanged(listener: SessionChangedListener): () => void {
  sessionChangedListeners.add(listener)
  return () => {
    sessionChangedListeners.delete(listener)
  }
}

// Préférences persistées rattachées à UN conseiller : elles survivent au logout, donc sur un poste partagé le
// suivant en hériterait. `STORAGE_RETURN_TO` est le cas le plus visible — en sortie de maintenance il pousse le
// nouvel arrivant vers la conversation de l'ancien. `customer-case-panel-mode` : mode du panneau du dossier client.
const USER_SCOPED_KEYS = [STORAGE_RETURN_TO, 'customer-case-panel-mode']
// Mode spectateur, une clé par conversation : même valeur que `STORAGE_KEY_PREFIX` des `spectator/useSpectatorMode.ts`
// des agents. Recopiée et non importée : importer un module d'agent depuis le shell couplerait le shell à l'agent.
const USER_SCOPED_PREFIXES = ['spectator-mode-']

/**
 * Reset les states partagés qui peuvent porter des données du précédent user.
 * À appeler dès qu'on change d'identité : login, logout, enter/exit impersonation.
 * Ne pas inclure le state d'auth lui-même — il est mis à jour par les fonctions appelantes.
 */
function clearUserScopedStores(): void {
  for (const listener of sessionChangedListeners) {
    try {
      listener()
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[useAuth] sessionChanged listener failed', err)
    }
  }
  try {
    for (const key of USER_SCOPED_KEYS) localStorage.removeItem(key)
    const scoped: string[] = []
    for (let index = 0; index < localStorage.length; index++) {
      const key = localStorage.key(index)
      if (key !== null && USER_SCOPED_PREFIXES.some((prefix) => key.startsWith(prefix))) scoped.push(key)
    }
    for (const key of scoped) localStorage.removeItem(key)
  } catch {
    // localStorage indisponible (navigation privée stricte) : sans conséquence ici.
  }
}

const STORAGE_ACCESS = 'sapiendo.auth.access'
const STORAGE_REFRESH = 'sapiendo.auth.refresh'
const STORAGE_USER = 'sapiendo.auth.user'
// Impersonation : on garde les tokens admin originaux pour pouvoir y revenir.
const STORAGE_IMP_ACCESS = 'sapiendo.auth.impersonator.access'
const STORAGE_IMP_REFRESH = 'sapiendo.auth.impersonator.refresh'
const STORAGE_IMP_USER = 'sapiendo.auth.impersonator.user'
// Page où revenir en quittant l'impersonation (ex. la liste des retours et ses filtres, SD-4638).
const STORAGE_IMP_RETURN_TO = 'sapiendo.auth.impersonator.return_to'

const accessToken = ref<string | null>(localStorage.getItem(STORAGE_ACCESS))
const refreshToken = ref<string | null>(localStorage.getItem(STORAGE_REFRESH))
const user = ref<AuthUser | null>((() => {
  const raw = localStorage.getItem(STORAGE_USER)
  if (!raw) return null
  try {
    return JSON.parse(raw) as AuthUser
  } catch {
    return null
  }
})())

const impersonator = ref<AuthUser | null>((() => {
  const raw = localStorage.getItem(STORAGE_IMP_USER)
  if (!raw) return null
  try {
    return JSON.parse(raw) as AuthUser
  } catch {
    return null
  }
})())

const isAuthenticated = computed(() => accessToken.value !== null && user.value !== null)
const isAdmin = computed(() => user.value?.is_admin === true)
const isImpersonating = computed(() => impersonator.value !== null)

function persist(): void {
  if (accessToken.value) localStorage.setItem(STORAGE_ACCESS, accessToken.value)
  else localStorage.removeItem(STORAGE_ACCESS)
  if (refreshToken.value) localStorage.setItem(STORAGE_REFRESH, refreshToken.value)
  else localStorage.removeItem(STORAGE_REFRESH)
  if (user.value) localStorage.setItem(STORAGE_USER, JSON.stringify(user.value))
  else localStorage.removeItem(STORAGE_USER)
}

export function setSession(payload: TokenResponse): void {
  clearUserScopedStores()
  accessToken.value = payload.access_token
  refreshToken.value = payload.refresh_token
  user.value = payload.user
  persist()
}

export function clearSession(): void {
  clearUserScopedStores()
  accessToken.value = null
  refreshToken.value = null
  user.value = null
  impersonator.value = null
  localStorage.removeItem(STORAGE_IMP_ACCESS)
  localStorage.removeItem(STORAGE_IMP_REFRESH)
  localStorage.removeItem(STORAGE_IMP_USER)
  localStorage.removeItem(STORAGE_IMP_RETURN_TO)
  persist()
}

/** Chemin interne à la sandbox (jamais une autre origine). */
function isInternalPath(path: string | undefined): path is string {
  return typeof path === 'string' && path.startsWith('/') && !path.startsWith('//')
}

/**
 * Bascule sur la session du user cible. Conserve les tokens admin courants pour pouvoir revenir.
 * `returnTo` : page où revenir à la sortie ; sans lui, la sortie ramène à l'écran par défaut.
 * L'appelant recharge ensuite la page : les vues d'agent lisent leurs jetons à leur chargement.
 */
export function enterImpersonation(payload: TokenResponse, opts: { returnTo?: string } = {}): void {
  if (isInternalPath(opts.returnTo)) localStorage.setItem(STORAGE_IMP_RETURN_TO, opts.returnTo)
  else localStorage.removeItem(STORAGE_IMP_RETURN_TO)
  // Sauvegarde l'admin actuel (s'il y en a un) — sera restauré au exitImpersonation().
  // Déjà en impersonation : on garde l'admin d'origine, jamais la session intermédiaire.
  if (localStorage.getItem(STORAGE_IMP_ACCESS) === null && accessToken.value && refreshToken.value && user.value) {
    localStorage.setItem(STORAGE_IMP_ACCESS, accessToken.value)
    localStorage.setItem(STORAGE_IMP_REFRESH, refreshToken.value)
    localStorage.setItem(STORAGE_IMP_USER, JSON.stringify(user.value))
    impersonator.value = user.value
  }
  clearUserScopedStores()
  accessToken.value = payload.access_token
  refreshToken.value = payload.refresh_token
  user.value = payload.user
  persist()
}

/**
 * Restaure la session admin originale. `restored` = une impersonation était en cours ;
 * `returnTo` = page mémorisée à l'entrée, ou `null`.
 */
export function exitImpersonation(): { restored: boolean; returnTo: string | null } {
  const access = localStorage.getItem(STORAGE_IMP_ACCESS)
  const refresh = localStorage.getItem(STORAGE_IMP_REFRESH)
  const userRaw = localStorage.getItem(STORAGE_IMP_USER)
  const storedReturn = localStorage.getItem(STORAGE_IMP_RETURN_TO) ?? undefined
  localStorage.removeItem(STORAGE_IMP_RETURN_TO)
  if (!access || !refresh || !userRaw) return { restored: false, returnTo: null }
  try {
    const adminUser = JSON.parse(userRaw) as AuthUser
    clearUserScopedStores()
    accessToken.value = access
    refreshToken.value = refresh
    user.value = adminUser
  } catch {
    return { restored: false, returnTo: null }
  }
  impersonator.value = null
  localStorage.removeItem(STORAGE_IMP_ACCESS)
  localStorage.removeItem(STORAGE_IMP_REFRESH)
  localStorage.removeItem(STORAGE_IMP_USER)
  persist()
  return { restored: true, returnTo: isInternalPath(storedReturn) ? storedReturn : null }
}

export function getAccessToken(): string | null {
  return accessToken.value
}

export function getRefreshToken(): string | null {
  return refreshToken.value
}

export function setAccessToken(token: string): void {
  accessToken.value = token
  persist()
}

export function setRefreshToken(token: string): void {
  refreshToken.value = token
  persist()
}

export function setUser(u: AuthUser): void {
  user.value = u
  persist()
}

export function useAuth(): {
  user: typeof user
  impersonator: typeof impersonator
  accessToken: typeof accessToken
  isAuthenticated: typeof isAuthenticated
  isAdmin: typeof isAdmin
  isImpersonating: typeof isImpersonating
  setSession: typeof setSession
  clearSession: typeof clearSession
  enterImpersonation: typeof enterImpersonation
  exitImpersonation: typeof exitImpersonation
} {
  return {
    user,
    impersonator,
    accessToken,
    isAuthenticated,
    isAdmin,
    isImpersonating,
    setSession,
    clearSession,
    enterImpersonation,
    exitImpersonation,
  }
}
